import { useMemo, useState } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { type SelectChangeEvent } from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import { MODELS } from 'src/utils/constants';

import { CustomPopover } from 'src/components/custom-popover';

import ModelInfoPopover from './SettingsModelMenu';
import { useSettingsContext } from '../hooks/useSettingsContext';

export type TModel = {
  id: string;
  name: string;
  description: string;
  fileTypeSupport: { label: string; value: string }[];
  knowledgeCutoff?: string;
  link: string;
};

type ModelGroup = {
  group: string;
  models: TModel[];
};

type SettingsModelSelectProps = {
  onChange?: (model: string) => void;
};

function SettingsModelSelect({ onChange }: SettingsModelSelectProps) {
  const popover = usePopover();
  const { settings } = useSettingsContext();
  const [hoveredModel, setHoveredModel] = useState<TModel>();

  const modelList = useMemo(
    () =>
      Object.entries(MODELS.models || {}).map(([key, _value]) => ({
        group: key,
        models: (_value as TModel[]).map((model) => ({
          id: model.id,
          name: model.name,
          description: model.description,
          fileTypeSupport: model.fileTypeSupport,
          knowledgeCutoff: model.knowledgeCutoff,
          link: model.link,
        })),
      })),
    []
  );

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange?.(event.target.value);
    popover.onClose();
  };

  const handleMouseOver = (event: React.MouseEvent<HTMLElement>, model: TModel) => {
    setHoveredModel(model);
    popover.onOpen(event);
  };

  const handleMenuExited = () => {
    setHoveredModel(undefined);
    popover.onClose();
  };

  return (
    <>
      <Select
        size="small"
        value={settings.model}
        onChange={handleChange}
        MenuProps={{
          TransitionProps: { onExited: handleMenuExited },
        }}
      >
        {modelList.length > 0 ? (
          modelList.map((modelGroup: ModelGroup) => [
            <ListSubheader key={`header-${modelGroup.group}`} sx={{ p: 0 }}>
              {modelGroup.group}
            </ListSubheader>,
            modelGroup.models.map((model) => (
              <MenuItem
                key={model.id}
                value={model.id}
                onMouseOver={(event) => handleMouseOver(event, model)}
              >
                {model.name}
              </MenuItem>
            )),
          ])
        ) : (
          <MenuItem value="" />
        )}
      </Select>

      <CustomPopover
        anchorEl={popover.anchorEl}
        open={popover.open}
        onClose={() => {
          setHoveredModel(undefined);
          popover.onClose();
        }}
        sx={{ margin: '0 -8px', pointerEvents: 'none' }}
        slotProps={{
          arrow: {
            placement: 'right-top',
            hide: true,
          },
        }}
      >
        {hoveredModel && (
          <ClickAwayListener
            onClickAway={() => {
              setHoveredModel(undefined);
              popover.onClose();
            }}
          >
            <Box>
              <ModelInfoPopover mockModel={hoveredModel} />
            </Box>
          </ClickAwayListener>
        )}
      </CustomPopover>
    </>
  );
}

export default SettingsModelSelect;
